/**
 * Judge.me accessibility fixes for the Minus33 storefront.
 *
 * Install this as the single Judge.me theme JavaScript customization. It replaces
 * the older interval-heavy customization; do not load both versions together.
 */
(function judgeMeAccessibility() {
  'use strict';

  var VERSION = '2026.09.14.1';
  var MODAL_SELECTOR = '.jm-mfp-wrap.jdgm-full-rev-modal';
  var INVALID_CONTROL_SELECTOR =
    '.jdgm-form__rating a[role="button"], ' +
    'input:not([type="hidden"]):not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [role="radio"][tabindex]:not([tabindex="-1"]), ' +
    '[contenteditable="true"]';
  var previous = window.__JUDGE_ME_A11Y__;
  if (previous && previous.version === VERSION) return;
  if (previous && typeof previous.destroy === 'function') previous.destroy();

  var observers = new Set();
  var observedWidgets = new WeakSet();
  var observedModals = new WeakSet();
  var timers = new Set();
  var labelRoots = new Set();
  var labelsScheduled = false;
  var modalReturnFocus = null;
  var activeModal = null;

  function later(callback, delay) {
    var id = window.setTimeout(function () {
      timers.delete(id);
      callback();
    }, delay);
    timers.add(id);
    return id;
  }

  function cleanText(element) {
    return element ? element.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function visibleText(element) {
    return element
      ? (element.innerText || element.textContent || '').replace(/\s+/g, ' ').trim()
      : '';
  }

  function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== String(value)) {
      element.setAttribute(name, String(value));
    }
  }

  function processAverageRatingLink(link) {
    var text = visibleText(link);
    if (!text) return;
    if (link.tagName === 'A' || link.hasAttribute('href') ||
      link.getAttribute('role') === 'link') {
      link.removeAttribute('aria-label');
    }
  }

  function processProductVariantLink(control) {
    var text = visibleText(control);
    if (!text) return;
    setAttribute(control, 'aria-label', text);
  }

  function processAverageRatingValue(value) {
    if (!/^\d+(?:\.\d+)?$/.test(visibleText(value))) return;
    value.removeAttribute('role');
    value.removeAttribute('aria-level');
    if (value.style.getPropertyValue('--text-size') !== 'var(--jm-body-md-size)') {
      value.style.setProperty('--text-size', 'var(--jm-body-md-size)');
    }
    if (value.style.getPropertyValue('--text-weight') !== 'var(--jm-body-md-weight-default)') {
      value.style.setProperty('--text-weight', 'var(--jm-body-md-weight-default)');
    }
  }

  function processVotingControls(root) {
    queryWithin(root, '.jm-voting-controls').forEach(function (controls) {
      var buttons = controls.querySelectorAll('.jm-voting-controls__button');
      Array.from(buttons).forEach(function (button) {
        var text = visibleText(button);
        if (text) setAttribute(button, 'aria-label', text);
      });
    });
  }

  function processTabs(root) {
    var tabs = root.querySelectorAll ? root.querySelectorAll('.jdgm-tab') : [];
    var panels = root.querySelectorAll
      ? root.querySelectorAll('.jdgm-tablist__content > *') : [];
    Array.from(tabs).forEach(function (tab, index) {
      var panelId = tab.getAttribute('aria-controls');
      var panel = panels[index];
      if (!panelId || !panel) return;
      if (panel.id !== panelId) panel.id = panelId;
      setAttribute(panel, 'role', 'tabpanel');
      if (tab.id) setAttribute(panel, 'aria-labelledby', tab.id);
    });
  }

  function getPseudoElementText(element, pseudoElement) {
  var content = window.getComputedStyle(element, pseudoElement)
    .getPropertyValue('content');

  if (!content || content === 'none' || content === 'normal') {
    return '';
  }

  return content
    .replace(/^["']|["']$/g, '')
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .trim();
}

function ensureVariantLabelFixStyles() {
  if (document.getElementById('jm-a11y-variant-label-styles')) return;

  var style = document.createElement('style');
  style.id = 'jm-a11y-variant-label-styles';
  style.textContent =
    '.jdgm-rev__variant-label[data-jm-a11y-fixed="true"]::before,' +
    '.jdgm-rev__variant-label[data-jm-a11y-fixed="true"]::after{' +
    'content:none!important;' +
    '}';

  document.head.appendChild(style);
}

function processReviewVariantLabels(root) {
  queryWithin(root, '.jdgm-rev__variant-label').forEach(function (label) {
    if (cleanText(label)) return;

    var beforeText = getPseudoElementText(label, '::before');
    var afterText = getPseudoElementText(label, '::after');
    var text = [beforeText, afterText]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text) return;

    label.textContent = text;
    label.setAttribute('data-jm-a11y-fixed', 'true');
  });
}


  function processJudgeMeLabelFixes(root) {
    queryWithin(root,
      '.jm-average-rating-display, .jm-average-rating-display--clickable'
    ).forEach(processAverageRatingLink);
    queryWithin(root,
      'p.jm-product-variant-card__product-link[role="button"]'
    ).forEach(processProductVariantLink);
    queryWithin(root, '.jm-average-rating-display .jm-text')
      .forEach(processAverageRatingValue);
    processVotingControls(root);
    processTabs(root);
    processReviewVariantLabels(root);
  }

  function scheduleJudgeMeLabelFixes(root) {
    labelRoots.add(root);
    if (labelsScheduled) return;
    labelsScheduled = true;
    requestAnimationFrame(function () {
      labelsScheduled = false;
      labelRoots.forEach(processJudgeMeLabelFixes);
      labelRoots.clear();
    });
  }

  function isVisible(element) {
    if (!element || element.hidden) return false;
    var style = window.getComputedStyle(element);
    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.getClientRects().length > 0;
  }

  function safeId(value) {
    return String(value || 'review')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'review';
  }

  function uniqueId(element, base) {
    if (element.id) {
      var existing = document.querySelectorAll('[id="' + CSS.escape(element.id) + '"]');
      if (existing.length === 1) return element.id;
    }

    var candidate = base;
    var suffix = 2;
    while (document.getElementById(candidate) &&
      document.getElementById(candidate) !== element) {
      candidate = base + '-' + suffix++;
    }
    element.id = candidate;
    return candidate;
  }

  function replaceTag(element, tagName) {
    if (!element || element.tagName.toLowerCase() === tagName) return element;
    var replacement = document.createElement(tagName);
    Array.from(element.attributes).forEach(function (attribute) {
      replacement.setAttribute(attribute.name, attribute.value);
    });
    while (element.firstChild) replacement.appendChild(element.firstChild);
    element.replaceWith(replacement);
    return replacement;
  }

  function queryWithin(node, selector) {
    var matches = [];
    if (node.nodeType !== 1) return matches;
    if (node.matches(selector)) matches.push(node);
    return matches.concat(Array.from(node.querySelectorAll(selector)));
  }

  function formActions(form) {
    return form.querySelector('.jdgm-form__fieldset-actions');
  }

  function restoreOriginalSubmitButton(form, buttonClass, cancelClass) {
    var actions = formActions(form);
    if (!actions) return;

    var widget = form.closest('.jdgm-review-widget') || document;
    var button = form.querySelector('.' + buttonClass);
    if (!button) {
      var candidates = widget.querySelectorAll('.' + buttonClass);
      if (candidates.length === 1) button = candidates[0];
    }
    if (!button) return;

    var nestedPatchWrapper = button.closest('.jdgm-no-dup');
    var cancel = actions.querySelector('.' + cancelClass);
    if (button.parentElement !== actions) {
      actions.insertBefore(button, cancel || null);
    }
    if (nestedPatchWrapper && !nestedPatchWrapper.children.length) {
      nestedPatchWrapper.remove();
    }

    if (button.style.display === 'none' && !button.disabled) {
      button.style.removeProperty('display');
    }
  }

  function processForm(form) {
    var isQuestion = !!form.closest('.jdgm-question-form-wrapper') ||
      !!form.querySelector('.jdgm-submit-question');

    if (isQuestion) {
      restoreOriginalSubmitButton(form, 'jdgm-submit-question', 'jdgm-cancel-question');
    } else {
      restoreOriginalSubmitButton(form, 'jdgm-submit-rev', 'jdgm-cancel-rev');
    }

    queryWithin(form, '.jdgm-form__title').forEach(function (title) {
      replaceTag(title, 'h3');
    });

    queryWithin(form, '.jdgm-form__reviewer-name-format-container').forEach(function (container) {
      var label = container.querySelector('.jdgm-form__inline-label');
      var select = container.querySelector('select');
      var labelText = cleanText(label);
      if (select && labelText) select.setAttribute('aria-label', labelText);
    });
  }

  function invalidControlForFieldset(fieldset) {
    var controls = fieldset.querySelectorAll(INVALID_CONTROL_SELECTOR);
    return Array.from(controls).find(isVisible) || null;
  }

  function firstInvalidControl(form) {
    var controls = Array.from(form.querySelectorAll(INVALID_CONTROL_SELECTOR));

    return controls.find(function (control) {
      if (!isVisible(control)) return false;
      if (control.getAttribute('aria-invalid') === 'true') return true;
      try {
        if (control.matches(':invalid')) return true;
      } catch (error) {
        // Older storefront engines may not support :invalid on every element.
      }
      var fieldset = control.closest('.jdgm-form__fieldset');
      if (!fieldset) return false;
      var visibleError = Array.from(fieldset.querySelectorAll('.jdgm-input-error'))
        .some(isVisible);
      return visibleError && invalidControlForFieldset(fieldset) === control;
    }) || null;
  }

  function focusFirstInvalidWhenReady(form) {
    if (!form || form.dataset.jdgmValidationWatch === 'true') return;
    form.dataset.jdgmValidationWatch = 'true';
    var attempts = 0;
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      observer.disconnect();
      delete form.dataset.jdgmValidationWatch;
    }

    function check() {
      if (finished || !form.isConnected) return finish();
      var hasValidationEvidence = !!form.querySelector(
        '[aria-invalid="true"], .jdgm-input-error'
      );
      if (!hasValidationEvidence) {
        attempts += 1;
        if (attempts >= 25) return finish();
        later(check, 80);
        return;
      }
      var invalid = firstInvalidControl(form);
      if (invalid) {
        if (invalid.matches('.jdgm-form__rating a[role="button"]') &&
          !invalid.hasAttribute('tabindex')) {
          invalid.setAttribute('tabindex', '0');
        }
        invalid.focus({ preventScroll: false });
        return finish();
      }
      attempts += 1;
      if (attempts >= 25) return finish();
      later(check, 80);
    }

    var observer = new MutationObserver(check);
    observer.observe(form, {
      attributes: true,
      attributeFilter: ['aria-invalid', 'class', 'hidden', 'style'],
      childList: true,
      subtree: true
    });
    observers.add(observer);
    Promise.resolve().then(check);
  }

  function reviewSort(widget) {
    return widget && widget.querySelector(
      '.jdgm-rev-widg__sort-wrapper select.jdgm-sort-dropdown'
    );
  }

  function focusSortAfterUpdate(widget) {
    var target = reviewSort(widget);
    if (!target) return;
    var attempts = 0;
    var settledFrames = 0;
    var changed = false;
    var initialSignature = '';
    var lastSignature = '';
    var observer;

    function signature() {
      var reviews = widget.querySelector('.jdgm-rev-widg__reviews');
      var clear = widget.querySelector('.jdgm-histogram__clear-filter');
      return (reviews ? reviews.innerHTML.length + ':' + reviews.childElementCount : '0') +
        '|' + (clear ? clear.className + ':' + clear.getAttribute('style') : '');
    }

    function finish() {
      if (observer) observer.disconnect();
      if (isVisible(target)) target.focus({ preventScroll: false });
    }

    function check() {
      if (!widget.isConnected) {
        if (observer) observer.disconnect();
        return;
      }
      var currentSignature = signature();
      if (currentSignature !== initialSignature) changed = true;
      settledFrames = currentSignature === lastSignature ? settledFrames + 1 : 0;
      lastSignature = currentSignature;
      attempts += 1;
      if ((changed && settledFrames >= 2) || attempts >= 24) return finish();
      later(check, 75);
    }

    initialSignature = signature();
    lastSignature = initialSignature;
    observer = new MutationObserver(function () {
      changed = true;
      settledFrames = 0;
    });
    observer.observe(widget, {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style'],
      childList: true,
      subtree: true
    });
    later(check, 0);
  }

  function processHistogram(widget) {
    queryWithin(widget, '.jdgm-histogram__row[data-frequency]').forEach(function (row) {
      var interactive = row.querySelector('.jdgm-histogram__star[role="button"]');
      var frequency = row.querySelector('.jdgm-histogram__frequency');
      if (!interactive) return;

      if (row.getAttribute('data-frequency') === '0') {
        if (row.hasAttribute('aria-hidden')) row.removeAttribute('aria-hidden');
        interactive.setAttribute('aria-disabled', 'true');
        interactive.setAttribute('tabindex', '-1');
        if (frequency) setAttribute(frequency, 'aria-hidden', 'true');
      } else {
        interactive.removeAttribute('aria-disabled');
        interactive.setAttribute('tabindex', '0');
        if (frequency) frequency.removeAttribute('aria-hidden');
      }
    });
  }

  function reviewerId(card, reviewer) {
    var reviewKey = safeId(card.getAttribute('data-review-id'));
    var scope = card.closest('.jdgm-review-snippet-widget-wrapper') || document;
    var matchingCards = Array.from(scope.querySelectorAll(
      '.jdgm-rev-snippet-card[data-review-id="' +
      CSS.escape(card.getAttribute('data-review-id') || '') + '"]'
    ));
    var ordinal = Math.max(0, matchingCards.indexOf(card)) + 1;
    return uniqueId(reviewer, 'jdgm-snippet-reviewer-' + reviewKey + '-' + ordinal);
  }

  function processSnippetCard(card) {
    var reviewer = card.querySelector('.jdgm-rev-snippet-card__reviewer');
    if (reviewer) {
      reviewer = replaceTag(reviewer, 'blockquote');
      reviewer.removeAttribute('aria-label');
    }

    var body = card.querySelector('.jdgm-rev-snippet-card__rev-body');
    if (body) {
      body = replaceTag(body, 'div');
      body.removeAttribute('aria-label');
    }

    var rating = card.querySelector('.jdgm-rev-snippet-card__rev-rating');
    if (rating) {
      rating.removeAttribute('tabindex');
      rating.removeAttribute('data-original-tabindex');
    }

    if (reviewer) {
      var id = reviewerId(card, reviewer);
      var link = card.querySelector('a.jdgm-rev-snippet-card__full-review-link');
      if (link) {
        link.removeAttribute('role');
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-describedby', id);
        if (!link.querySelector('[data-jdgm-full-review-text]')) {
          var hiddenText = document.createElement('span');
          hiddenText.className = 'visually-hidden';
          hiddenText.dataset.jdgmFullReviewText = 'true';
          hiddenText.textContent = 'View the full review';
          link.appendChild(hiddenText);
        }
      }
    }
  }

  function setDescendantTabOrder(card) {
    var hidden = card.getAttribute('aria-hidden') === 'true';
    if (!card.hasAttribute('data-jdgm-card-tabindex')) {
      card.dataset.jdgmCardTabindex = card.getAttribute('tabindex') || '';
    }
    if (hidden) {
      card.setAttribute('tabindex', '-1');
    } else if (card.dataset.jdgmCardTabindex) {
      card.setAttribute('tabindex', card.dataset.jdgmCardTabindex);
    } else {
      card.removeAttribute('tabindex');
    }

    card.querySelectorAll(
      'a, button, input, select, textarea, [contenteditable="true"], [tabindex]'
    ).forEach(function (control) {
      if (control.classList.contains('jdgm-rev-snippet-card__rev-rating')) {
        control.removeAttribute('tabindex');
        return;
      }
      if (!control.hasAttribute('data-jdgm-tabindex')) {
        control.dataset.jdgmTabindex = control.getAttribute('tabindex') || '';
      }
      if (hidden) {
        control.setAttribute('tabindex', '-1');
      } else if (control.dataset.jdgmTabindex) {
        control.setAttribute('tabindex', control.dataset.jdgmTabindex);
      } else {
        control.removeAttribute('tabindex');
      }
    });
  }

  function processCarousel(wrapper) {
    var widget = wrapper.matches('.jdgm-review-snippet-widget')
      ? wrapper
      : wrapper.querySelector('.jdgm-review-snippet-widget');
    if (!widget) return;
    widget.setAttribute('role', 'region');
    if (!widget.getAttribute('aria-label')) {
      widget.setAttribute('aria-label', 'Customer reviews');
    }

    [
      ['.jdgm-rev-snippet-widget__prev-btn', 'Previous reviews'],
      ['.jdgm-rev-snippet-widget__next-btn', 'Next reviews']
    ].forEach(function (definition) {
      var controls = Array.from(widget.querySelectorAll(definition[0]));
      var nativeButtons = controls.filter(function (control) {
        return control.tagName === 'BUTTON';
      });
      controls.forEach(function (control) {
        if (nativeButtons.length && control.tagName !== 'BUTTON') {
          setAttribute(control, 'aria-hidden', 'true');
          control.setAttribute('tabindex', '-1');
          return;
        }
        control.setAttribute('aria-label', definition[1]);
        if (control.tagName !== 'BUTTON') {
          control.setAttribute('role', 'button');
          control.setAttribute('tabindex', '0');
        }
      });
    });

    queryWithin(widget, '.jdgm-rev-snippet-card').forEach(function (card) {
      processSnippetCard(card);
      setDescendantTabOrder(card);
    });
  }

  function shownModal(modal) {
    return isVisible(modal) &&
      !modal.classList.contains('jm-mfp-removing');
  }

  function modalParts(modal) {
    return {
      close: modal.querySelector('.jm-mfp-close'),
      view: modal.querySelector('.jdgm-full-rev__product-button')
    };
  }

  function processModal(modal) {
    // Guard: only process actual Judge.me modals, never document.body
    if (!modal || !modal.matches || !modal.matches(MODAL_SELECTOR)) return;

    var dialog = modal.querySelector('.jdgm-full-rev') || modal;
    var reviewer = dialog.querySelector('.jdgm-full-rev__reviewer-name');
    var timestamp = dialog.querySelector('.jdgm-full-rev__timestamp');
    var ids = [];

    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('tabindex', '-1');
    if (reviewer) ids.push(uniqueId(reviewer, 'jdgm-full-review-reviewer'));
    if (timestamp) ids.push(uniqueId(timestamp, 'jdgm-full-review-timestamp'));
    if (ids.length) modal.setAttribute('aria-labelledby', ids.join(' '));

    dialog.querySelectorAll(
      '.jdgm-full-rev__rating[tabindex], .jdgm-full-rev__rating [tabindex]'
    ).forEach(function (rating) {
      rating.removeAttribute('tabindex');
    });

    var parts = modalParts(modal);
    [parts.close, parts.view].filter(Boolean).forEach(function (control) {
      if (control.tagName !== 'BUTTON' && control.tagName !== 'A') {
        control.setAttribute('tabindex', '0');
      } else {
        control.removeAttribute('tabindex');
      }
    });

    if (shownModal(modal) && activeModal !== modal) {
      activeModal = modal;
      modalReturnFocus = document.activeElement;
      later(function () {
        if (shownModal(modal) && parts.close) {
          parts.close.focus({ preventScroll: true });
        }
      }, 0);
    } else if (!shownModal(modal) && activeModal === modal) {
      activeModal = null;
      if (modalReturnFocus && modalReturnFocus.isConnected) modalReturnFocus.focus();
      modalReturnFocus = null;
    }
  }

  function observeModal(modal) {
    if (observedModals.has(modal)) return;
    observedModals.add(modal);
    var observer = new MutationObserver(function () {
      processModal(modal);
    });
    observer.observe(modal, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true
    });
    observers.add(observer);
  }

  function processWidget(widget) {
    if (widget.matches('.jdgm-review-widget')) {
      processHistogram(widget);
      queryWithin(widget, '.jdgm-form').forEach(processForm);
    }
    if (widget.matches(
      '.jdgm-review-snippet-widget-wrapper, .jdgm-review-snippet-widget'
    )) {
      processCarousel(widget);
    }
  }

  function processNode(node) {
    if (!node || node.nodeType !== 1) return;
    processJudgeMeLabelFixes(node);
    scheduleJudgeMeLabelFixes(node);
    var nearestWidget = node.closest(
      '.jdgm-review-widget, .jdgm-review-snippet-widget-wrapper'
    );
    if (nearestWidget) {
      processWidget(nearestWidget);
      observeWidget(nearestWidget);
    }
    var nearestForm = node.closest('.jdgm-form');
    if (nearestForm) processForm(nearestForm);
    var nearestCard = node.closest('.jdgm-rev-snippet-card');
    if (nearestCard) {
      processSnippetCard(nearestCard);
      setDescendantTabOrder(nearestCard);
    }
    var nearestModal = node.closest(MODAL_SELECTOR);
    if (nearestModal) {
      processModal(nearestModal);
      observeModal(nearestModal);
    }

    queryWithin(node,
      '.jdgm-review-widget, .jdgm-review-snippet-widget-wrapper'
    ).forEach(function (widget) {
      processWidget(widget);
      observeWidget(widget);
    });
    queryWithin(node, '.jdgm-form').forEach(processForm);
    queryWithin(node, '.jdgm-rev-snippet-card').forEach(function (card) {
      processSnippetCard(card);
      setDescendantTabOrder(card);
    });
    queryWithin(node, MODAL_SELECTOR).forEach(function (modal) {
      processModal(modal);
      observeModal(modal);
    });
  }

  function observeWidget(widget) {
    if (observedWidgets.has(widget)) return;
    observedWidgets.add(widget);
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') processNode(mutation.target);
        mutation.addedNodes.forEach(processNode);
      });
    });
    observer.observe(widget, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'data-frequency', 'style'],
      childList: true,
      subtree: true
    });
    observers.add(observer);
  }

  function onClick(event) {
    var zeroHistogram = event.target.closest(
      '.jdgm-histogram__row[data-frequency="0"] .jdgm-histogram__star[role="button"]'
    );
    if (zeroHistogram) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var histogramControl = event.target.closest(
      '.jdgm-histogram__star[role="button"], .jdgm-histogram__clear-filter[role="button"]'
    );
    if (histogramControl) {
      var widget = histogramControl.closest('.jdgm-review-widget');
      if (widget) focusSortAfterUpdate(widget);
    }

    var reviewSubmit = event.target.closest('.jdgm-submit-rev');
    if (reviewSubmit) focusFirstInvalidWhenReady(reviewSubmit.closest('form, .jdgm-form'));
  }

  function onKeydown(event) {
    if (activeModal && shownModal(activeModal) && event.key === 'Tab') {
      var parts = modalParts(activeModal);
      var focusables = [parts.close, parts.view].filter(function (element) {
        return element && isVisible(element);
      });
      if (!focusables.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      var index = focusables.indexOf(document.activeElement);
      index = event.shiftKey
        ? (index <= 0 ? focusables.length - 1 : index - 1)
        : (index < 0 || index === focusables.length - 1 ? 0 : index + 1);
      focusables[index].focus({ preventScroll: true });
      return;
    }

    var zeroHistogram = event.target.closest(
      '.jdgm-histogram__row[data-frequency="0"] .jdgm-histogram__star[role="button"]'
    );
    if (zeroHistogram && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var carouselControl = event.target.closest(
      '.jdgm-rev-snippet-widget__prev-btn[role="button"], ' +
      '.jdgm-rev-snippet-widget__next-btn[role="button"], ' +
      'button.jdgm-rev-snippet-widget__prev-btn, ' +
      'button.jdgm-rev-snippet-widget__next-btn'
    );
    if (carouselControl && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      carouselControl.click();
      return;
    }

    var histogramControl = event.target.closest(
      '.jdgm-histogram__star[role="button"], .jdgm-histogram__clear-filter[role="button"]'
    );
    if (histogramControl && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      histogramControl.click();
    }
  }

  function onSubmit(event) {
    if (event.target.matches('.jdgm-form') &&
      !event.target.closest('.jdgm-question-form-wrapper')) {
      focusFirstInvalidWhenReady(event.target);
    }
  }

  function onInvalid(event) {
    var form = event.target.closest('.jdgm-form');
    if (form && !form.closest('.jdgm-question-form-wrapper')) {
      focusFirstInvalidWhenReady(form);
    }
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('invalid', onInvalid, true);

  function cleanupBodyAttributes() {
    // Remove any invalid dialog attributes previously applied to <body>
    // by the modalObserver regression in version 2026.08.21.1
    ['role', 'aria-modal', 'tabindex'].forEach(function (attr) {
      document.body.removeAttribute(attr);
    });
  }

  function start() {
    ensureVariantLabelFixStyles();
    // Clean up attributes mistakenly applied to <body> by the previous version
    cleanupBodyAttributes();

    var stableRoot = document.getElementById('MainContent') ||
      document.querySelector('main') ||
      document.body;
    processNode(stableRoot);
    if (stableRoot !== document.body) processNode(document.body);

    var widgetBootstrap = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(processNode);
      });
    });
    widgetBootstrap.observe(stableRoot, { childList: true, subtree: true });
    observers.add(widgetBootstrap);

    // Judge.me appends its modal as a top-level body child. The attribute
    // observer is restricted to actual Judge.me modal elements only to
    // prevent processModal() from firing on document.body itself.
    var modalObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' &&
            mutation.target.matches &&
            mutation.target.matches(MODAL_SELECTOR)) {
          processModal(mutation.target);
        }
        mutation.addedNodes.forEach(processNode);
      });
    });
    modalObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true
    });
    observers.add(modalObserver);
  }

  function destroy() {
    observers.forEach(function (observer) { observer.disconnect(); });
    observers.clear();
    timers.forEach(window.clearTimeout);
    timers.clear();
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('keydown', onKeydown, true);
    document.removeEventListener('submit', onSubmit, true);
    document.removeEventListener('invalid', onInvalid, true);
  }

  window.__JUDGE_ME_A11Y__ = {
    version: VERSION,
    destroy: destroy,
    refresh: function () { processNode(document.body); }
  };

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });

  console.info('[Judge.me a11y] initialized', VERSION);
})();
